import { useCallback, useMemo } from 'react';
import { useFormManager } from './useFormManager';
import { useTableOperations } from '../../../../../hooks/useTableOperations';
import { useTagPickerOperations } from '../../../../../hooks/useTagPickerOperations';
import { useSearchOperations } from '../../../../../hooks/useSearchOperations';
import { useClientMatterOperations } from '../../../../../hooks/useClientMatterOperations';
import { SectionKey } from '../../../../../types';
import { getSectionFlow } from '../useFormState';
import {
  EnhancedFieldConfig,
  EnhancedGroupedSection,
  EnhancedSection,
  FormSchemaConfig,
  isEnhancedGroupedSection,
} from '../../types';

const isClientMatterSchemaField = (
  field: unknown
): field is EnhancedFieldConfig => {
  return (
    typeof field === 'object' &&
    field !== null &&
    'type' in field &&
    (field as { type?: unknown }).type === 'clientMatter'
  );
};

const processTableSchemaClientMatterFields = (
  fields: unknown[] | undefined,
  processClientMatterField: (
    field: EnhancedFieldConfig
  ) => EnhancedFieldConfig
): unknown[] | undefined => {
  if (!fields) {
    return fields;
  }

  return fields.map((schemaField) =>
    isClientMatterSchemaField(schemaField)
      ? processClientMatterField(schemaField)
      : schemaField
  );
};

const processTableSchemaClientMatterSections = (
  sections: unknown[] | undefined,
  processClientMatterField: (
    field: EnhancedFieldConfig
  ) => EnhancedFieldConfig
): unknown[] | undefined => {
  if (!sections) {
    return sections;
  }

  return sections.map((section) => {
    if (typeof section !== 'object' || section === null) {
      return section;
    }

    const typedSection = section as {
      fields?: unknown[];
      sections?: unknown[];
    };

    return {
      ...typedSection,
      fields: processTableSchemaClientMatterFields(
        typedSection.fields,
        processClientMatterField
      ),
      sections: processTableSchemaClientMatterSections(
        typedSection.sections,
        processClientMatterField
      ),
    };
  });
};

const attachClientMatterHandlersToTableSchema = (
  field: EnhancedFieldConfig,
  processClientMatterField: (
    field: EnhancedFieldConfig
  ) => EnhancedFieldConfig
): EnhancedFieldConfig => {
  const tableFormSchema = field.extras?.table?.tableFormSchema;
  const sections = tableFormSchema?.formSchema?.sections;
  if (!tableFormSchema || !sections) {
    return field;
  }

  return {
    ...field,
    extras: {
      ...field.extras,
      table: {
        ...field.extras?.table,
        tableFormSchema: {
          ...tableFormSchema,
          formSchema: {
            ...tableFormSchema.formSchema,
            sections: processTableSchemaClientMatterSections(
              sections,
              processClientMatterField
            ),
          },
        },
      },
    },
  };
};

/**
 * Enhanced form configuration hook that combines form management with UI-specific functionality
 *
 * @returns Form configuration and UI handlers
 */
export const useFormConfiguration = () => {
  const formManager = useFormManager({
    enableValidation: true,
    enableCalculations: true,
    enableDependencyProcessing: true,
    debugMode: false,
  });

  const {
    formState,
    processFields,
    processSections,
    updateFormData,
    updateSectionState,
    sectionProgress,
    schemas, // Extract schemas to use in getEnhancedSectionSchema
    // Extract calculation-related functions
    handleCalculations,
    processFormData,
    processSchema,
  } = formManager;

  // Create a hash of key dependency fields to force schema recalculation when they change
  // This avoids infinite loops while ensuring schema updates when dependencies change
  const dependencyFieldsHash = useMemo(() => {
    if (!formState.data) return '';
    const keyFields = [
      'payments_count',
      'interest',
      'interest_options',
      'payment_total',
      'confirm_approval_overtime_request',
    ];
    const values = keyFields.map((field) => formState.data?.[field]).join('|');
    return values;
  }, [formState.data]);

  const { processTableField } = useTableOperations();
  const { processTagPickerField, tagPickerState } = useTagPickerOperations();
  const { processSearchField, searchState, isSearchReady } =
    useSearchOperations();
  // Call useClientMatterOperations directly
  const { processClientMatterField, clientMatterState } =
    useClientMatterOperations(formState.office);

  // Helper to get the first section if none is selected
  const getDefaultSection = useCallback((): SectionKey => {
    const currentFlow = getSectionFlow(formState.data);
    return currentFlow[0];
  }, [formState.data]);

  // Enhanced process fields with table, tagPicker, and search handling
  const enhancedProcessFields = useCallback(
    (fields: EnhancedFieldConfig[]) => {
      // Apply dependency processing to fields
      let processedFields = processFields(fields);

      // Then apply table, tagPicker, search, and clientMatter processing
      processedFields = processedFields.map((field) => {
        let processedField = { ...field };

        if (field.type === 'table') {
          processedField = processTableField(processedField);
          processedField = attachClientMatterHandlersToTableSchema(
            processedField,
            processClientMatterField
          );
        }
        if (field.type === 'tagPicker') {
          processedField = processTagPickerField(processedField);
        }
        if (field.type === 'search') {
          processedField = processSearchField(processedField);
        }
        if (field.type === 'clientMatter') {
          console.log(
            '🔍 useFormConfiguration processing clientMatter field:',
            field.name
          );
          processedField = processClientMatterField(processedField);
          console.log(
            '🔍 useFormConfiguration processed clientMatter field result:',
            {
              fieldName: processedField.name,
              hasClientMatter: !!processedField.extras?.clientMatter,
              clientMatterKeys: Object.keys(
                processedField.extras?.clientMatter || {}
              ),
            }
          );
        }

        return processedField;
      });

      return processedFields;
    },
    [
      processFields,
      processTableField,
      processTagPickerField,
      processSearchField,
      processClientMatterField,
    ]
  );

  // Recursively apply field enhancements to nested grouped sections
  const processEnhancedSection = useCallback(
    (
      section: EnhancedSection | EnhancedGroupedSection
    ): EnhancedSection | EnhancedGroupedSection => {
      if (isEnhancedGroupedSection(section)) {
        return {
          ...section,
          sections: section.sections.map((subSection) =>
            processEnhancedSection(subSection)
          ),
        };
      }

      return {
        ...section,
        fields: section.fields
          ? enhancedProcessFields(section.fields)
          : [],
      };
    },
    [enhancedProcessFields]
  );

  // Get schema for a section, with dynamic payment details logic for all workflow types
  const getEnhancedSectionSchema = useCallback(
    (section: SectionKey): FormSchemaConfig | null => {
      if (!schemas) return null;

      const schema = schemas[section as keyof typeof schemas];
      if (!schema) {
        return schemas[getDefaultSection() as keyof typeof schemas];
      }

      // Check if this section should include payment details based on workflow type
      const shouldIncludePaymentDetails = [
        // Dubai Payment Approval workflow sections
        'resp_lawyer',
        'first_partner',
        'second_partner',
        'payment_confirmation',
        // Dubai Payment From Office Account workflow sections
        'dubai_payment_from_office_account_approver',
        'dubai_payment_from_office_account_payment_confirmation',
        // Dubai Transfer Requisition workflow sections
        'dubai_transfer_requisition_first_partner',
        'dubai_transfer_requisition_second_partner',
        'dubai_transfer_requisition_payment_confirmation',
        // Overtime Approval workflow sections
        'overtime_approval_line_manager',
        'overtime_approval_finance_confirmation',
      ].includes(section);

      if (shouldIncludePaymentDetails) {
        // Get the appropriate payment details schema based on workflow type
        let paymentSchemaKey: SectionKey | null = null;
        let sectionsToInclude: number[] = [];

        // Determine which payment details schema to use and which sections to include
        if (section.includes('dubai_payment_from_office_account')) {
          paymentSchemaKey =
            'dubai_payment_from_office_account_payment_details';
          sectionsToInclude = [1, 2, 3];
        } else if (section.includes('dubai_transfer_requisition')) {
          paymentSchemaKey = 'dubai_transfer_requisition_payment_details';
          // For Dubai Transfer Requisition: show sections [0], [1], and [2]
          sectionsToInclude = [0, 1, 2];
        } else if (section.includes('overtime_approval')) {
          paymentSchemaKey = 'overtime_approval_request_details';
          // Employee Details [0] and Request Details with overtime grid [1]
          sectionsToInclude = [0, 1];
        } else {
          // Default to standard payment details for Dubai Payment Approval
          paymentSchemaKey = 'payment_details';
          sectionsToInclude = [1, 2];
        }

        const paymentSchema = schemas[paymentSchemaKey];
        if (
          paymentSchema?.sections?.[0] &&
          isEnhancedGroupedSection(paymentSchema.sections[0])
        ) {
          const paymentGroupSection = paymentSchema.sections[0];

          // Get the sections that should be included based on workflow type
          const sectionsToRender = sectionsToInclude
            .map((sectionIndex) => paymentGroupSection.sections[sectionIndex])
            .filter(Boolean) // Remove any undefined sections
            .map((section) => ({
              ...section,
              extras: {
                ...section.extras,
                defaultExpanded: true,
              },
            }));

          if (sectionsToRender.length > 0) {
            const modifiedPaymentGroup: EnhancedGroupedSection = {
              ...paymentGroupSection,
              isGrouped: true,
              title: paymentGroupSection.title,
              sections: sectionsToRender,
            };

            return {
              ...schema,
              sections: [modifiedPaymentGroup, ...(schema.sections || [])],
            } as FormSchemaConfig;
          }
        }
      }
      return schema;
    },
    [schemas, getDefaultSection]
  );

  // Current form schema, always processed here (no mutation outside)
  // CRITICAL: Include dependencyProcessingVersion to force recalculation when visibility changes
  const processedFormSchema = useMemo(() => {
    if (!formState.data) return null;

    // If no current section is set, use the first section
    const sectionKey = formState.currentSection || getDefaultSection();
    const schema = getEnhancedSectionSchema(sectionKey);

    if (!schema) return { sections: [] };

    // For payment_details, skip section-level dependency processing and rely on field-level processing.
    // Other sections continue to use section-level processing which works correctly.
    const isPaymentDetailsSection = sectionKey === 'payment_details';
    const sectionsSource = schema.sections
      ? isPaymentDetailsSection
        ? schema.sections
        : processSections(schema.sections)
      : [];

    return {
      sections: sectionsSource
        ? sectionsSource.map((section) => processEnhancedSection(section))
        : [],
    };
  }, [
    formState.data,
    formState.currentSection,
    getDefaultSection,
    getEnhancedSectionSchema,
    processEnhancedSection,
    processSections,
  ]);

  // Function to run calculations on the current form data
  const runCalculations = useCallback(async () => {
    if (!formState.currentSection || !formState.data) return;
    // Get the schema for the current section
    const schema = getEnhancedSectionSchema(formState.currentSection);
    if (!schema) return;

    // Process the schema
    const processedSchema = processSchema(
      schema,
      formState.data,
      formState.currentSection
    );

    // Run calculations
    const calculatedData = await handleCalculations(
      processedSchema,
      formState.data
    );

    // Update form data with calculated values if there are any
    if (Object.keys(calculatedData).length > 0) {
      console.log('Updating form with calculated values:', calculatedData);
      updateFormData({
        ...formState.data,
        ...calculatedData,
      });
    }
  }, [
    formState.currentSection,
    formState.data,
    getEnhancedSectionSchema,
    processSchema,
    handleCalculations,
    updateFormData,
  ]);

  // Additional props for the form
  
  const additionalProps = useMemo(
    () => ({
      tagPickerSuggestions: tagPickerState.suggestions,
      loadingFields: tagPickerState.loadingFields,
      minSearchLength: 3,
      preSelectedTags: {},
      // Add search state
      searchOptions: searchState.searchOptions,
      searchLoadingFields: searchState.loadingFields,
      searchDropdownStates: searchState.dropdownStates,
      // Add clientMatter state
      clientMatterState,
      // CRITICAL: Pass updateFormData so FormField can sync changes to PaymentApproval state immediately
      // This ensures dependency processing sees field value changes in real-time, not just component library state
      updateFormData,
    }),
    [
      tagPickerState.suggestions,
      tagPickerState.loadingFields,
      searchState.searchOptions,
      searchState.loadingFields,
      searchState.dropdownStates,
      clientMatterState,
      updateFormData,
    ]
  );

  // Check if all operations are ready
  const isFormReady = useMemo(() => {
    return isSearchReady && processedFormSchema;
  }, [isSearchReady, processedFormSchema]);

  // The final form config
  const formConfig = useMemo(() => {
    if (!processedFormSchema) return null;

    return {
      formSchema: processedFormSchema,
      initialData: formState.data,
      validateOnChange: true,
      validateOnBlur: true,
      enableAutoFocusNext: false,
      additionalProps,
    };
  }, [processedFormSchema, formState.data, additionalProps]);

  // Section for progress bar, etc.
  const progressSection = useMemo(() => {
    if (formState.currentSection) return formState.currentSection;
    if (formState.completedSections.length > 0)
      return formState.completedSections[0];
    return getDefaultSection();
  }, [
    formState.currentSection,
    formState.completedSections,
    getDefaultSection,
  ]);

  const createdBy = useMemo(() => {
    if (formState.data.createdBy) return formState.data.createdBy;
  }, [formState.data.createdBy]);

  return {
    formConfig,
    formManager,
    currentSection: formState.currentSection || getDefaultSection(),
    progressSection,
    createdBy,
    completedSections: formState.completedSections,
    sectionProgress,
    updateSectionState,
    // Expose calculation functions
    runCalculations,
    handleCalculations,
    processFormData,
    // Expose field processing functions
    enhancedProcessFields,
    processEnhancedSection,
    // Loading state
    isFormReady,
  };
};
